<script setup>
import { ref, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import IconChevronBottom from '@martyrs/src/modules/icons/navigation/IconChevronBottom.vue'
import Icon from '@martyrs/src/design-system/components/Icon/Icon.vue'

import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js'
import * as auth from '@martyrs/src/modules/auth/views/store/auth.js'

const store = useStore()
const router = useRouter()
const route = useRoute()

import Dropdown from '@martyrs/src/design-system/components/Dropdown/Dropdown.vue'
import Spoiler from '@martyrs/src/design-system/components/Spoiler/Spoiler.vue'

const props = defineProps({
  navigationItems: Array,
  theme: {
    type: String,
    default: "light"
  },
  horizontal: {
    type: Boolean,
    default: false
  }
})

const stateSidebar = defineModel('stateSidebar', { type: Boolean, default: false })

const expandedSections = ref(
  (props.navigationItems || [])
    .flatMap(section => section.items)
    .filter(item => item.expanded && item.subItems)
    .map(item => item.title)
)

const toggleSection = (title) => {
  if (!stateSidebar.value) {
    if (menuItems.value.flatMap(section => section.items).find(item => item.title === title && item.subItems)) {
      stateSidebar.value = true
      setTimeout(() => {
        expandedSections.value.push(title)
      }, 50)
    }
    return
  }
  
  const index = expandedSections.value.indexOf(title)

  if (index === -1) {
    expandedSections.value.push(title)
  } else {
    expandedSections.value.splice(index, 1)
  }
}

const isSectionExpanded = (title) => {
  return expandedSections.value.includes(title)
}

// Process route functions with auth data and route
const processRoute = (routeFn) => {
  if (typeof routeFn === 'function') {
    return routeFn(auth.state, route)
  }
  return routeFn
}

// Универсальный клик-handler: `#section` → smooth-scroll к id, всё остальное —
// vue-router push. Это даёт хедер-навигации работать с якорными ссылками на
// лендинге (`#how`, `#pricing`, `#reviews`) без отдельной обёртки.
const navigate = (target) => {
  if (!target) return
  if (typeof target === 'string' && target.startsWith('#')) {
    if (typeof document === 'undefined') return
    const scrollview = document.getElementById('scrollview')
    const el = scrollview?.querySelector(target) || document.querySelector(target)
    if (el) {
      if (scrollview) {
        const top = el.getBoundingClientRect().top + scrollview.scrollTop
        scrollview.scrollTo({ top, behavior: 'smooth' })
      } else {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    }
    return
  }
  if (route.path !== target) router.push(target)
}

</script>

<template>
  <nav
    class="ease-quint-out"
    :class="[
      stateSidebar ? 'pd-small' : 'pd-micro',
      horizontal ? 'w-100 desktop-only gap-thin flex flex-center flex-nowrap o-y-visible o-x-visible' : 'gap-medium flex flex-column',
    ]"
  >
    <div 
      v-for="section in props.navigationItems" 
      v-show="!section.visible || (typeof section.visible === 'function' ? section.visible(auth.state, route) : true)"
      :key="section.category" 
      class="ease-quint-out"
      :class="[
        horizontal ? 'w-max  flex-child-default flex flex-nowrap' : '',
        stateSidebar && !horizontal ? '' : '',
      ]"
    >
      <div
        v-if="section.category && stateSidebar && !horizontal"
        class="uppercase fw-medium opacity-50 p-small"
        :class="{
          't-black': theme === 'light',
          't-white':  theme === 'dark'
        }"
      >
        {{ section.category }}
      </div>

      <ul 
        :class="[
          horizontal ? 'w-100 gap-micro flex flex-nowrap' : 'flex flex-column gap-micro',
        ]"
      >
        <li
          v-for="item in section.items"
          v-show="!item.visible || (typeof item.visible === 'function' ? item.visible(auth.state, route) : true)"
          class="w-100 pos-relative"
          :key="item.title"

        >
        <Dropdown
          v-if="(item.subItems || item.dropdownComponent) && horizontal"
          class="w-100"
          :align="item.dropdownComponent ? 'center' : 'left'"
          trigger="hover"
        >
          <template #label>
            <button
              class="ease-quint-out w-100 flex radius-small flex-nowrap cursor-pointer flex-v-center pd-thin"
              @click="item.route ? navigate(processRoute(item.route)) : null"
              :class="[
                item.route && route.path === processRoute(item.route) ? theme === 'light' ? 'bg-light' : 'bg-dark' : '',
                theme === 'light' ? 'hover:bg-light' : 'hover:bg-dark'
              ]"
            >
              <span class="fw-medium">{{ item.title }}</span>
              <IconChevronBottom :fill="theme === 'dark' ? 'rgb(var(--white))': 'rgb(var(--black))'" class="i-small mn-l-thin"/>
            </button>
          </template>
          <!-- Кастомный компонент дропдауна — хост-проект прокидывает свой
               рендерер (мега-меню, формы и т.п.) через item.dropdownComponent.
               Navigation остаётся тупым контейнером с Dropdown'ом и пропсы
               НЕ пробрасывает — компонент инкапсулирует всё в себе. -->
          <component v-if="item.dropdownComponent" :is="item.dropdownComponent" />
          <ul v-else class="dropdown-submenu">
            <li v-for="subItem in item.subItems" :key="subItem.title">
              <a
                @click.prevent="subItem.route ? navigate(processRoute(subItem.route)) : null"
                href="#"
                class="group ease-quint-out flex flex-v-center flex-nowrap gap-thin pd-thin radius-small"
                :class="[
                  subItem.route && route.path === processRoute(subItem.route) ? theme === 'light' ? 'bg-light' : 'bg-dark' : '',
                  theme === 'light' ? 'hover:bg-light' : 'hover:bg-dark'
                ]"
              >
                <Icon
                  v-if="typeof subItem.icon === 'string'"
                  :name="subItem.icon"
                  class="i-medium t-black-transp-25 group-hover:t-main"
                />
                <component
                  v-else-if="subItem.icon"
                  :is="subItem.icon"
                  class="i-medium t-black-transp-25 group-hover:t-main"
                />
                <span class="w-100">{{ subItem.title }}</span>
              </a>
            </li>
          </ul>
        </Dropdown>
        <button
          v-else
          class="group ease-quint-out w-100 flex radius-small flex-nowrap cursor-pointer flex-v-center"
          @click="item.route ? (isPhone() ? (stateSidebar = false) : null, navigate(processRoute(item.route))) : null"
          :class="[
              horizontal ? 'pd-thin' : '',
              item.route && route.path === processRoute(item.route) ? theme === 'light' ? 'bg-light' : 'bg-dark' : '',
              theme === 'light' ? 'hover:bg-light' : 'hover:bg-dark'
            ]"
        >
          <div v-if="!horizontal && item.icon" class="w-100 flex-child-default w-max-big flex flex-center aspect-1x1 pos-relative">
            <Icon
              v-if="typeof item.icon === 'string'"
              :name="item.icon"
              class="i-medium flex-child-default ease-quint-out t-black-transp-25 group-hover:t-main"
            />
            <component
              v-else-if="item.icon"
              :is="item.icon"
              class="i-medium flex-child-default ease-quint-out t-black-transp-25 group-hover:t-main"
            />
            <span
              v-if="!stateSidebar && Number(item.badge) > 0"
              class="nav-badge-dot"
              :title="String(item.badge)"
              aria-hidden="true"
            />
          </div>

          <span
            class="ease-quint-out w-100 t-left fw-medium"
            :class="{ 'pd-small': !horizontal, 'hidden': !stateSidebar && !horizontal, 'visible': stateSidebar || horizontal }"
          >
            {{ item.title }}<span v-if="(stateSidebar || horizontal) && Number(item.badge) > 0" class="nav-badge-count"> ({{ item.badge }})</span>
          </span>

          <span
            v-if="item.subItems && stateSidebar && !horizontal"
            class="mn-l-auto mn-r-small sidebar-dropdown-icon"
            :class="{ 'rotate-180deg': isSectionExpanded(item.title) }"
            @click.stop="toggleSection(item.title)"
          >
             <IconChevronBottom :class="{ 'rotate-180deg mn-t-micro-negative': isOpen }" :fill=" theme === 'dark' ? 'rgb(var(--white))': 'rgb(var(--black))'" class="i-small"/>
          </span>
        </button>
        <transition
          enter-active-class="dropdown-enter"
          leave-active-class="dropdown-leave"
          enter-from-class="dropdown-enter-from"
          enter-to-class="dropdown-enter-to"
          leave-from-class="dropdown-leave-from"
          leave-to-class="dropdown-leave-to"
        >
          <ul
            v-if="item.subItems && isSectionExpanded(item.title) && stateSidebar && !horizontal"
            class="mn-l-small mn-t-thin"
          >
            <li v-for="subItem in item.subItems" :key="subItem.title">

              <a
               @click.prevent="subItem.route ? (isPhone() ? (stateSidebar = false) : null, navigate(processRoute(subItem.route))) : null"
                href="#"
                class="group ease-quint-out flex flex-v-center flex-nowrap gap-thin pd-thin radius-small"
                :class="[
                  subItem.route && route.path === processRoute(subItem.route) ? theme === 'light' ? 'bg-light' : 'bg-dark' : '',
                    theme === 'light' ? 'hover:bg-light' : 'hover:bg-dark'
                    ]"
              >
                <Icon
                  v-if="typeof subItem.icon === 'string'"
                  :name="subItem.icon"
                  class="i-medium t-black-transp-25 group-hover:t-main"
                />
                <component
                  v-else-if="subItem.icon"
                  :is="subItem.icon"
                  class="i-medium t-black-transp-25 group-hover:t-main"
                />
                <span class="w-100">{{ subItem.title }}</span>
              </a>
            </li>
          </ul>
        </transition>
        </li>
      </ul>
    </div>
  </nav>
</template>

<style scoped>

.rotate-180deg {
  transform: rotate(180deg);
}

.nav-badge-count {
  color: rgb(var(--main));
  font-weight: 600;
}

.nav-badge-dot {
  position: absolute;
  top: 0.15rem;
  right: 0.15rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: rgb(var(--main));
  box-shadow: 0 0 0 2px rgb(var(--light, 255, 255, 255));
  pointer-events: none;
}

.hidden {
/*  opacity: 0;*/
  width: 0;
  display: none;
}

.visible {
/*  opacity: 1;*/
  display: block;
}

/* Dropdown animations */
.dropdown-enter, .dropdown-leave {
  transition: all 0.3s ease-in-out;
}

.dropdown-enter-from, .dropdown-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

.dropdown-enter-to, .dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* Horizontal dropdown submenu */
.dropdown-submenu {
  list-style: none;
  padding: 0.5rem;
  margin: 0;
  min-width: 12rem;
}

.dropdown-submenu li {
  width: 100%;
}

.dropdown-submenu a {
  display: flex;
  text-decoration: none;
  white-space: nowrap;
}

/* Transition for horizontal dropdown */
:deep(.TransitionTranslateY-enter-active),
:deep(.TransitionTranslateY-leave-active) {
  transition: translateY 0.2s ease;
   transition: opacity 0.2s ease;
}

:deep(.TransitionTranslateY-enter-from),
:deep(.TransitionTranslateY-leave-to) {
  opacity: 0;
  transform: translateY(-0.5rem);
}

:deep(.TransitionTranslateY-enter-to),
:deep(.TransitionTranslateY-leave-from) {
  opacity: 1;
  transform: translateY(0);
}
</style>